import React, { useState } from 'react';
import { Button, Typography, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconTruckDelivery, IconFileText, IconFlag, IconTruckReturn, IconClipboardCheck } from '@tabler/icons-react';
import { message } from '../services/toastMessage';
import { markTransportStarted, declareOffRent, markCollectionStarted } from '../services/BookingService';
import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { ICheckInOutReport } from '../interfaces/heavyDutyMachinery/ICheckInOut';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { CORAL } from '../theme/creamyTokens';

const { Title, Text } = Typography;

type ActionKind =
  | { kind: 'call'; run: (bookingId: string) => Promise<IBooking> }
  | { kind: 'navigate'; to: (bookingId: string) => string };

interface LifecycleAction {
  key: string;
  icon: React.ElementType;
  labelKey: string;
  detailKey: string;
  action: ActionKind;
}

interface BookingLifecycleActionsProps {
  booking: IBooking;
  reports: ICheckInOutReport[];
  isHost: boolean;
  /** Own user id -- needed to tell "I wrote this report" from "the other party did". */
  userId?: string;
  onBookingChanged: (booking: IBooking) => void;
}

/**
 * The one thing this viewer can do to move the rental forward, right now.
 *
 * Deliberately ONE action, chosen first-match-wins from an ordered list, in
 * the same spirit as the timeline having exactly one current phase: at any
 * given moment a rental is waiting on exactly one person to do exactly one
 * thing, and a card offering three buttons is a card that has stopped
 * telling you which. If nothing matches, this renders nothing at all --
 * "nothing for you to do" is a perfectly good state and deserves silence
 * rather than a disabled button.
 *
 * The order below is the rental's own order, and each entry states who it
 * belongs to. Anything to do with an albarán routes away to that report's
 * own page -- writing one is far too big for a card, and answering one
 * means looking at the photos first, which is the entire point of the
 * exercise. The rest are single milestone posts and happen right here.
 */
const BookingLifecycleActions: React.FC<BookingLifecycleActionsProps> = ({
  booking,
  reports,
  isHost,
  userId,
  onBookingChanged,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [running, setRunning] = useState(false);

  const checkIn = reports.find((r) => r.reportType === 'CheckIn');
  const checkOut = reports.find((r) => r.reportType === 'CheckOut');

  // A report is mine to answer when someone else wrote it and nobody has
  // answered it yet -- the same rule the backend enforces in
  // CheckInOutController.CanAcknowledge, mirrored here so the button simply
  // doesn't appear rather than appearing and then 403ing.
  const awaitingMyAnswer = (report?: ICheckInOutReport) =>
    !!report && !report.acknowledgementOutcome && !!userId && report.submittedByUserId !== userId;

  const handedOver = !!booking.handedOverAt;

  const candidates: LifecycleAction[] = [];

  // -- Host: the machine sets off. Only for a booking that actually has a
  //    transport leg; a self-pickup rental goes straight to the albarán.
  if (isHost && booking.status === 'Confirmed' && booking.deliveryRequested && !booking.transportStartedAt) {
    candidates.push({
      key: 'transport',
      icon: IconTruckDelivery,
      labelKey: TRANSLATIONS.LIFECYCLE_START_TRANSPORT,
      detailKey: TRANSLATIONS.LIFECYCLE_START_TRANSPORT_DETAIL,
      action: { kind: 'call', run: markTransportStarted },
    });
  }

  // -- Host: write the delivery note, once the machine is (or is on its way)
  //    there. This is the capture flow that already exists.
  if (isHost && booking.status === 'Confirmed' && !checkIn) {
    candidates.push({
      key: 'check-in',
      icon: IconFileText,
      labelKey: TRANSLATIONS.LIFECYCLE_DELIVERY_NOTE,
      detailKey: TRANSLATIONS.LIFECYCLE_DELIVERY_NOTE_DETAIL,
      action: { kind: 'navigate', to: (id) => `/heavy-duty-machinery/bookings/${id}/check-in-out/CheckIn` },
    });
  }

  // -- Counterparty: answer the delivery note. Ahead of everything below,
  //    because until it is answered the handover hasn't happened and none
  //    of the later steps are true yet. Routes to the report's own page
  //    rather than opening a sheet here: nobody should be putting their
  //    name to a delivery note they haven't been shown.
  if (awaitingMyAnswer(checkIn)) {
    candidates.push({
      key: 'ack-check-in',
      icon: IconClipboardCheck,
      labelKey: TRANSLATIONS.LIFECYCLE_REVIEW_DELIVERY_NOTE,
      detailKey: TRANSLATIONS.LIFECYCLE_REVIEW_DELIVERY_NOTE_DETAIL,
      action: { kind: 'navigate', to: (id) => `/heavy-duty-machinery/bookings/${id}/check-in-out/CheckIn` },
    });
  }

  // -- Guest: fin de obra. Stops the clock without needing anyone to turn up.
  if (!isHost && booking.status === 'Confirmed' && handedOver && !booking.offRentAt) {
    candidates.push({
      key: 'off-rent',
      icon: IconFlag,
      labelKey: TRANSLATIONS.LIFECYCLE_DECLARE_OFF_RENT,
      detailKey: TRANSLATIONS.LIFECYCLE_DECLARE_OFF_RENT_DETAIL,
      action: { kind: 'call', run: declareOffRent },
    });
  }

  // -- Host: set off to collect, once the guest has finished with it.
  if (isHost && booking.offRentAt && booking.returnPickupRequested && !booking.collectionStartedAt) {
    candidates.push({
      key: 'collection',
      icon: IconTruckReturn,
      labelKey: TRANSLATIONS.LIFECYCLE_START_COLLECTION,
      detailKey: TRANSLATIONS.LIFECYCLE_START_COLLECTION_DETAIL,
      action: { kind: 'call', run: markCollectionStarted },
    });
  }

  // -- Host: the return note. Same capture flow, other end of the rental.
  if (isHost && booking.offRentAt && !checkOut) {
    candidates.push({
      key: 'check-out',
      icon: IconFileText,
      labelKey: TRANSLATIONS.LIFECYCLE_RETURN_NOTE,
      detailKey: TRANSLATIONS.LIFECYCLE_RETURN_NOTE_DETAIL,
      action: { kind: 'navigate', to: (id) => `/heavy-duty-machinery/bookings/${id}/check-in-out/CheckOut` },
    });
  }

  // -- Counterparty: answer the return note.
  if (awaitingMyAnswer(checkOut)) {
    candidates.push({
      key: 'ack-check-out',
      icon: IconClipboardCheck,
      labelKey: TRANSLATIONS.LIFECYCLE_REVIEW_RETURN_NOTE,
      detailKey: TRANSLATIONS.LIFECYCLE_REVIEW_RETURN_NOTE_DETAIL,
      action: { kind: 'navigate', to: (id) => `/heavy-duty-machinery/bookings/${id}/check-in-out/CheckOut` },
    });
  }

  const next = candidates[0];

  const handleClick = async () => {
    if (!next) return;
    if (next.action.kind === 'navigate') {
      navigate(next.action.to(booking.id));
      return;
    }
    setRunning(true);
    try {
      onBookingChanged(await next.action.run(booking.id));
      message.success(translate(t, TRANSLATIONS.LIFECYCLE_ACTION_SUCCESS));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.LIFECYCLE_ACTION_ERROR));
    } finally {
      setRunning(false);
    }
  };

  if (!next) return null;

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 16,
        padding: 16,
      }}
    >
      <Title level={5} style={{ margin: '0 0 12px' }}>
        {translate(t, TRANSLATIONS.LIFECYCLE_NEXT_STEP_TITLE)}
      </Title>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
        <span
          style={{
            display: 'flex',
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            background: CORAL,
            color: '#fff',
          }}
        >
          {React.createElement(next.icon, { size: 20 })}
        </span>
        <div style={{ minWidth: 0 }}>
          <Text strong style={{ display: 'block', fontSize: 15 }}>
            {translate(t, next.labelKey)}
          </Text>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {translate(t, next.detailKey)}
          </Text>
        </div>
      </div>
      <Button block type="primary" size="large" loading={running} onClick={handleClick} style={{ borderRadius: 999 }}>
        {translate(t, next.labelKey)}
      </Button>
    </div>
  );
};

export default BookingLifecycleActions;
